// pages/HomePage.jsx
//
// Staff Home ("Business overview"), on GET /admin/home/overview (backend:
// leads/home_api.py + leads/home_overview.py). One request per range: the
// backend builds every block from one database snapshot, so nothing on this
// screen can contradict anything else on it. Replaces DashboardPage.jsx,
// which called /dashboard/* routes that never existed.
//
// The range lives in the query string (?range= for a preset, ?from=&to= for
// a custom one), so a view can be shared.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import api from '../services/api'
import { useAuth } from '../context/AppContext'
import { FALLBACK_TIMEZONE, fmtClock } from '../components/staff-leads/model'
import RangeSelect from '../components/staff-leads/RangeSelect'
import { parseUrlRange, rangeParams, rangeSearch } from '../utils/dateRange'
import {
  AUTO_REFRESH_MS, DEFAULT_RANGE, ENDPOINT, HOME_RESPONSIVE_CSS, RANGE_PRESETS, firstName, greeting,
} from '../components/home/model'
import HomeKpiTiles from '../components/home/HomeKpiTiles'
import AttentionCard from '../components/home/AttentionCard'
import LeadFlowCard from '../components/home/LeadFlowCard'
import RevenueDailyChart from '../components/home/RevenueDailyChart'
import { BuyersTable, TopPartnersTable } from '../components/home/HomeTables'
import MoneyCards from '../components/home/MoneyCards'

const REFRESH_TOAST_ID = 'home-refresh'

export default function HomePage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const range = parseUrlRange(searchParams, RANGE_PRESETS, DEFAULT_RANGE)
  const setRange = (next) => setSearchParams(rangeSearch(next, DEFAULT_RANGE), { replace: true })
  // A string, so the request effect fires on a change of range, not on
  // every render's new { from, to }.
  const paramsKey = JSON.stringify(rangeParams(range))
  const [tick, setTick] = useState(0)
  // `data` survives a refetch, so a refresh or a range change never blanks
  // the screen; a response for a range no longer selected is dropped.
  const [state, setState] = useState({ data: null, loading: false, error: null })

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    let cancelled = false
    setState((s) => ({ ...s, loading: true }))
    api.get(ENDPOINT, { params: JSON.parse(paramsKey) })
      .then(({ data }) => {
        if (!cancelled) setState({ data, loading: false, error: null })
      })
      .catch((error) => {
        if (!cancelled) setState((s) => ({ ...s, loading: false, error }))
      })
    return () => { cancelled = true }
  }, [paramsKey, tick])

  // ── auto-refresh: every range, never while the browser tab is hidden ──
  const lastLoadedRef = useRef(0)
  useEffect(() => {
    if (state.data) lastLoadedRef.current = Date.now()
  }, [state.data])

  useEffect(() => {
    const visible = () => document.visibilityState !== 'hidden'
    const interval = setInterval(() => { if (visible()) refresh() }, AUTO_REFRESH_MS)
    // Coming back to a tab that sat hidden past a cycle refreshes at once.
    const onVisibility = () => {
      if (visible() && Date.now() - lastLoadedRef.current >= AUTO_REFRESH_MS) refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refresh])

  // A failed refresh keeps what is on screen and says so once.
  const refreshError = state.data && state.error
  useEffect(() => {
    if (refreshError) toast.error('Could not refresh the overview - showing the last data loaded.', { id: REFRESH_TOAST_ID })
  }, [refreshError])

  const ov = state.data
  const timezone = ov?.timezone || FALLBACK_TIMEZONE
  const tzLabel = timezone === FALLBACK_TIMEZONE ? 'ET' : timezone
  const name = firstName(user)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{HOME_RESPONSIVE_CSS}</style>

      <header className="hm-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
            {greeting(new Date(), timezone)}{name ? `, ${name}` : ''}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            All times {tzLabel}
            {ov?.generated_at && <> · Updated {fmtClock(ov.generated_at, timezone)} {tzLabel}</>}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <RangeSelect value={range} onChange={setRange} presets={RANGE_PRESETS} allowCustom />
          <button type="button" className="btn btn-ghost btn-sm" onClick={refresh} disabled={state.loading} aria-label="Refresh">
            <RefreshCw size={14} aria-hidden="true" />
          </button>
        </div>
      </header>

      {!ov && state.error ? (
        <section className="card" role="alert" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)' }}>The business overview could not be loaded.</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={refresh} disabled={state.loading}>Retry</button>
        </section>
      ) : !ov ? (
        <Skeleton />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: state.loading ? 0.7 : 1, transition: 'opacity .15s' }}>
          <HomeKpiTiles kpis={ov.kpis} />
          <div className="hm-split">
            <AttentionCard alerts={ov.alerts || []} />
            <LeadFlowCard flow={ov.flow} />
          </div>
          <RevenueDailyChart rows={ov.revenue_daily} />
          <div className="hm-split">
            <TopPartnersTable rows={ov.top_partners} />
            <BuyersTable rows={ov.buyers} notice={ov.buyers_notice} />
          </div>
          <MoneyCards money={ov.money} />
        </div>
      )}
    </div>
  )
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading overview" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="hm-kpis">
        {[0, 1, 2, 3].map((i) => <div key={i} className="hm-skeleton" style={{ height: 110 }} />)}
      </div>
      <div className="hm-split">
        <div className="hm-skeleton" style={{ height: 220 }} />
        <div className="hm-skeleton" style={{ height: 220 }} />
      </div>
      <div className="hm-skeleton" style={{ height: 240 }} />
      <div className="hm-split">
        <div className="hm-skeleton" style={{ height: 220 }} />
        <div className="hm-skeleton" style={{ height: 220 }} />
      </div>
    </div>
  )
}
